import { Container, Graphics, Sprite, type Texture } from "pixi.js";
import { GRID_SIZE } from "../gridSize";

/**
 * The grid's geometry in design units, centred on the cells' centre, the composition root's origin (the shell mounts its
 * grid loading overlay there). Measured on the layout example (`Supporting Files/game layout example.png`, a phone 914.5
 * units wide, where the composition is drawn at 0.879): six columns and rows on a 158-unit pitch, a 146-unit tile under
 * each cell; the frame art drawn 1012 square (its white rim 98.8 % of the art: 879 units on that phone, as drawn there),
 * 4 units above the cells' centre (the example's rim sits 7 units above it, the art's rim 3 above its own centre); its
 * inner panel — the clip the symbols fall in and out of — 982 square with a 64-unit corner, the art's border scaled.
 */
export const GRID_GEOMETRY = {
  columns: GRID_SIZE.columns,
  rows: GRID_SIZE.rows,
  pitch: 158,
  tile: 146,
  tileAlpha: 0.32,
  frame: 1012,
  frameOffsetY: -4,
  panel: 982,
  panelRadius: 64,
} as const;

/** The grid's layout area: exactly six cells on each side, so the layer never resizes a cell. */
export const GRID_AREA_SIZE = { width: GRID_GEOMETRY.columns * GRID_GEOMETRY.pitch, height: GRID_GEOMETRY.rows * GRID_GEOMETRY.pitch } as const;

/**
 * The grid as a container: the frame, a tile under every cell, the panel clip holding the cell underlay, the symbol
 * `gridRoot` and the badge layer above the symbols, and the overlay above the clip. A slam tap is the shell's (its
 * full-screen surface over the scene), so nothing here is interactive.
 */
export class GridContainer extends Container {
  /** The grid's layout root (the land bounce and the layer's fit act on it). */
  public readonly gridArea: Container;
  /** The cells' parent, inside the panel clip. */
  public readonly gridRoot: Container;
  /** Under the symbols, inside the clip, in `gridRoot`'s own space. */
  public readonly cellUnderlay: Container;
  /** Over the symbols, inside the clip, in `gridRoot`'s own space: the cell multiplier badges. */
  public readonly badgeLayer: Container;
  /** Over the clip (nothing here is cut by the panel's edge). */
  public readonly gridOverlay: Container;
  /** The tile under each cell, by the cell's index (row × columns + column); empty without the tile art. */
  private readonly tiles: Sprite[] = [];

  public constructor(input: { frame: Texture | null; tile: Texture | null }) {
    super({ label: "GridContainer" });
    const g = GRID_GEOMETRY;

    this.gridArea = new Container({ label: "GridArea" });
    this.addChild(this.gridArea);

    const frameLayer = new Container({ label: "GridFrameLayer" });
    if (input.frame) {
      const frame = new Sprite({ texture: input.frame, anchor: 0.5, label: "Frame" });
      frame.setSize(g.frame, g.frame);
      frame.position.set(0, g.frameOffsetY);
      frameLayer.addChild(frame);
    }

    const tiles = new Container({ label: "CellTiles" });
    if (input.tile) {
      for (let row = 0; row < g.rows; row++) {
        for (let col = 0; col < g.columns; col++) {
          const tile = new Sprite({ texture: input.tile, anchor: 0.5, alpha: g.tileAlpha, label: `Tile:${row * g.columns + col}` });
          tile.setSize(g.tile, g.tile);
          tile.position.set((col - (g.columns - 1) / 2) * g.pitch, (row - (g.rows - 1) / 2) * g.pitch);
          tiles.addChild(tile);
          this.tiles.push(tile);
        }
      }
    }

    const panel = new Container({ label: "GridPanel" });
    const clip = new Graphics().roundRect(-g.panel / 2, g.frameOffsetY - g.panel / 2, g.panel, g.panel, g.panelRadius).fill(0xffffff);
    clip.label = "PanelClip";
    panel.addChild(clip);
    panel.mask = clip;
    this.cellUnderlay = new Container({ label: "CellUnderlay" });
    this.gridRoot = new Container({ label: "GridRoot" });
    this.badgeLayer = new Container({ label: "CellMultipliers" });
    panel.addChild(this.cellUnderlay, this.gridRoot, this.badgeLayer);

    this.gridOverlay = new Container({ label: "GridOverlay" });
    this.gridArea.addChild(frameLayer, tiles, panel, this.gridOverlay);
  }

  /** The tile under a cell (the cell multiplier badges colour it); null without the tile art. */
  public tileAt(positionIndex: number): Sprite | null {
    return this.tiles[positionIndex] ?? null;
  }

  public dispose(): void {
    this.destroy({ children: true });
  }
}
